// Feature spec 17, phase 8 (spec 11 6.15 item 39): the plan review of an origin deployment says what its apply does for
// its routes behind Cloudflare ("web accepts only Cloudflare's addresses and checks its secret header").
import { useQueries } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useApi } from '../../api/context';
import { queryKeys } from '../../api/queries';
import type { DeploymentOrigin } from '../../api/types';
import { strings } from '../../strings';

const cf = strings.networks.entryPoints.cloudflare;

/** The sentences of the origins whose Cloudflare route waits for this deployment's apply. */
function useCloudflareLockdowns(origins: readonly DeploymentOrigin[] | undefined): string[] {
  const api = useApi();
  const networkIds = [...new Set((origins ?? []).map((o) => o.networkId))];
  const networks = useQueries({
    queries: networkIds.map((id) => ({
      queryKey: queryKeys.network(id),
      queryFn: () => api.getNetwork(id),
      staleTime: 30_000,
    })),
  });
  const kindOf = (o: DeploymentOrigin) =>
    networks
      .find((q) => q.data?.data.id === o.networkId)
      ?.data?.data.settings.entryPoints.find((ep) => ep.id === o.entryPointId)?.kind;
  const sentences = (origins ?? [])
    .filter((o) => o.originState === 'waiting-for-apply' && kindOf(o) === 'cloudflare')
    .map((o) =>
      o.access === 'allow-list-and-header'
        ? cf.lockdown(o.resourceId)
        : o.access === 'header'
          ? cf.lockdownHeader(o.resourceId)
          : null,
    )
    .filter((x): x is string => !!x);
  return [...new Set(sentences)];
}

export function CloudflareLockdowns({ origins }: { origins: readonly DeploymentOrigin[] | undefined }) {
  const sentences = useCloudflareLockdowns(origins);
  if (!sentences.length) return null;
  return (
    <ul className="callout callout-info" data-testid="plan-cloudflare-lockdown">
      {sentences.map((text) => (
        <li key={text} className="flex items-start gap-2">
          <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>{text}.</span>
        </li>
      ))}
    </ul>
  );
}
